import type {ReactElement} from 'react';
import React from 'react';
import type {GammaColorType} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-gamma-option/base-gamma-option.types';
import {BaseGammaAdjustmentOption} from '@Panels/poster-editor-item-adjustments-panel/components/base-gamma-adjustment-option';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';
import {useGetKeyFromGammaOfActiveSlideItem} from '@Hooks/poster-editor/useGetKeyFromGammaOfActiveSlideItem';
import {useGetKeyFromGammaOfActiveItem} from '@Hooks/poster-editor/useGetKeyFromGammaOfActiveItem';

interface GammaAdjustmentOptionProps {
  panelId: string;
}

export function GammaAdjustmentOption({...props}: GammaAdjustmentOptionProps): ReactElement | null {
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeSlideUID = useGetSelectedSlideId();
  const activeSlide = activeSlideUID && activeItem?.isSlideshow() ? activeItem.getSelectedSlide() : null;

  const activeItemGammaValue = useGetKeyFromGammaOfActiveItem('isEnabled');
  const activeItemGammaRed = useGetKeyFromGammaOfActiveItem('red');
  const activeItemGammaGreen = useGetKeyFromGammaOfActiveItem('green');
  const activeItemGammaBlue = useGetKeyFromGammaOfActiveItem('blue');

  const isEnabled = useGetKeyFromGammaOfActiveSlideItem('isEnabled');
  const gammaRed = useGetKeyFromGammaOfActiveSlideItem('red');
  const gammaGreen = useGetKeyFromGammaOfActiveSlideItem('green');
  const gammaBlue = useGetKeyFromGammaOfActiveSlideItem('blue');

  if (!activeItem) {
    return null;
  }

  if (activeItem.isSlideshow() && !activeSlideUID) {
    return null;
  }

  const onToggleGamma = (): void => {
    if ('effects' in activeItem) {
      void activeItem.updateFromObject({
        effects: {
          gamma: {
            isEnabled: !activeItemGammaValue,
          },
        },
      });
    }
  };

  const onToggleGammaForSlideItem = (): void => {
    if (activeItem.isSlideshow() && activeSlide?.isMediaSlide() && activeSlideUID !== undefined) {
      void activeItem.updateSlideFromObject(activeSlideUID, {
        effects: {
          gamma: {
            isEnabled: !isEnabled,
          },
        },
      });
    }
  };

  const updateItemColor = (color: GammaColorType, updatedValue: number, undoable = true): void => {
    if ('effects' in activeItem) {
      void activeItem.updateFromObject(
        {
          effects: {
            gamma: activeItem.effects.getPartialUpdatedGammaObjectFromParams(updatedValue, color),
          },
        },
        {
          undoable,
        }
      );
    }
  };

  const updateSlideItemColor = (color: GammaColorType, updatedValue: number, undoable = true): void => {
    if (activeItem.isSlideshow() && activeSlide?.isMediaSlide() && activeSlideUID !== undefined) {
      void activeItem.updateSlideFromObject(
        activeSlideUID,
        {
          effects: {
            gamma: activeSlide.effects.getPartialUpdatedGammaObjectFromParams(updatedValue, color),
          },
        },
        {
          undoable,
        }
      );
    }
  };

  const getOptions = (): ReactElement | null => {
    if (activeItem.isSlideshow() && activeSlide?.isMediaSlide() && isEnabled !== undefined && gammaRed !== undefined && gammaBlue !== undefined && gammaGreen !== undefined) {
      return (
        <BaseGammaAdjustmentOption
          panelId={props.panelId}
          gammaObject={{isEnabled, red: gammaRed, blue: gammaBlue, green: gammaGreen}}
          onToggleGamma={onToggleGammaForSlideItem}
          updateColor={updateSlideItemColor}
        />
      );
    }

    if (activeItemGammaValue !== undefined && activeItemGammaBlue !== undefined && activeItemGammaGreen !== undefined && activeItemGammaRed !== undefined) {
      return (
        <BaseGammaAdjustmentOption
          panelId={props.panelId}
          gammaObject={{
            green: activeItemGammaGreen,
            red: activeItemGammaRed,
            blue: activeItemGammaBlue,
            isEnabled: activeItemGammaValue,
          }}
          onToggleGamma={onToggleGamma}
          updateColor={updateItemColor}
        />
      );
    }

    return null;
  };

  return getOptions();
}
